// Local-only recorder.
//
// =====================================================================
// PRIVACY HARD RULE
// This recorder is intentionally frontend-only. Do not add upload, fetch,
// WebSocket, analytics, or backend persistence for recording data. Every
// path in this file produces a Blob that ends up at the user's browser
// download — and nothing else. If a future change introduces network
// transmission for recording data, that change is wrong by spec.
// =====================================================================
//
// Architecture
//
//   ┌──────────────────┐     borrowed (read-only)
//   │ VideoSourceMgr   │──────────────────────────────────────────┐
//   └──────────────────┘                                          │
//                                                                 ▼
//   getRecordableSources() ───► RecorderController ───► one SourceRecorder
//                                       │                   per active slot
//                                       │                          │
//                                       │     each SourceRecorder  │
//                                       │     clones the source    │
//                                       │     track and feeds      │
//                                       │     MediaRecorder.       │
//                                       ▼                          ▼
//                              ZipRecordingWriter ◄─── chunks (Blob → Uint8Array)
//                                       │
//                                       │  + manifest.json
//                                       │  + README.txt
//                                       │  + debug/debug-log.{ndjson,json}
//                                       ▼
//                                fflate.zip(...) → Uint8Array
//                                       │
//                                       ▼
//                                 Blob download
//
// State machine:
//   off                 ──armWorkflowCapture──► workflow-armed
//   workflow-armed      ──disarmWorkflowCapture──► off
//   workflow-armed      ──startWorkflowCapture──► workflow-recording
//   workflow-recording  ──stopWorkflowCapture──► finalizing ──► off
//   off                 ──startFreeCapture──► free-recording
//   free-recording      ──stopFreeCapture──► finalizing ──► off
//   any failure         ────────────────────► error (and tries to finalize)
//
// Two important invariants:
//   1. The recorder NEVER touches the original MediaStream's tracks. It
//      always works on `track.clone()` so resetting a slot in the UI
//      (which calls `track.stop()` on the original) cannot kill the
//      recording, and the recorder's stop cannot kill the live preview.
//   2. The recorder NEVER calls fetch / WebSocket / sendBeacon. The static
//      grep gate in slice 6 enforces this by file pattern.

import { addDebugEventListener, logDebug } from "./debug.js";
// Vendored fflate (browser ESM build) — bare specifier `"fflate"` does not
// resolve when the backend serves raw ES modules, so we ship a copy under
// js/vendor/. Vite still bundles it through this relative import.
import { zip as fflateZip } from "./vendor/fflate.js";

// ---- exported helpers ------------------------------------------------------

export function nowIso(now = Date.now()) {
  return new Date(now).toISOString();
}

// Sanitize a label so it is safe inside a ZIP path on Windows/macOS/Linux.
// Replaces forbidden characters, collapses whitespace, and trims trailing
// dots/spaces (which Windows refuses). Forbidden set: backslash, slash,
// colon, asterisk, question, double-quote, less-than, greater-than, pipe.
const FORBIDDEN_NAME_CHARS = /[\\/:*?"<>|]+/g;
export function safeFileName(input, fallback = "source") {
  const raw = String(input == null ? "" : input);
  const cleaned = raw
    .replace(FORBIDDEN_NAME_CHARS, "_")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/g, "");
  if (!cleaned) return fallback;
  // Cap at 64 chars to keep the archive readable.
  return cleaned.length > 64 ? cleaned.slice(0, 64) : cleaned;
}

// Pick the best MediaRecorder mime type the platform supports. Returns:
//   - a non-empty string ("video/webm;codecs=vp9", etc.) if a known codec
//     is supported,
//   - the empty string "" if MediaRecorder exists but no preferred codec
//     was acknowledged (caller should construct MediaRecorder with no
//     mimeType so the browser picks its own default),
//   - null if MediaRecorder is unavailable entirely.
export function chooseMediaRecorderMimeType(
  MediaRecorderCtor = typeof MediaRecorder !== "undefined" ? MediaRecorder : null,
) {
  if (!MediaRecorderCtor) return null;
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  if (typeof MediaRecorderCtor.isTypeSupported === "function") {
    for (const t of candidates) {
      try {
        if (MediaRecorderCtor.isTypeSupported(t)) return t;
      } catch (_) {
        /* keep iterating */
      }
    }
  }
  return "";
}

// ---- ZIP writer ------------------------------------------------------------

// fflate-backed writer with a tiny surface so tests can swap in a fake.
// `add()` accepts a Uint8Array and per-entry options ({ level: 0..9 }).
// Media entries (already-compressed webm) should pass `{ level: 0 }` so
// fflate stores them; text/json entries can use the default level.
export class ZipRecordingWriter {
  constructor() {
    this._entries = Object.create(null);
    this._closed = false;
  }

  add(name, data, opts = {}) {
    if (this._closed) {
      throw new Error("ZipRecordingWriter: cannot add after build()");
    }
    if (!(data instanceof Uint8Array)) {
      throw new TypeError("ZipRecordingWriter: data must be a Uint8Array");
    }
    this._entries[name] = [data, opts];
  }

  async build() {
    if (this._closed) {
      throw new Error("ZipRecordingWriter: build() may only be called once");
    }
    this._closed = true;
    return await new Promise((resolve, reject) => {
      fflateZip(this._entries, { level: 6 }, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
  }
}

// ---- per-source recorder ---------------------------------------------------

const TIMESLICE_MS = 1000;

class SourceRecorder {
  constructor({ source, mimeType, MediaRecorderCtor }) {
    this.source = source;
    this.mimeType = mimeType;
    this.MediaRecorderCtor = MediaRecorderCtor;

    this.id = source.id;
    this.index = source.index;
    this.kind = source.kind;
    this.label = source.label;
    this.isMain = !!source.isMain;
    this.intrinsicWidth = source.width || 0;
    this.intrinsicHeight = source.height || 0;
    this.trackSettings = source.trackSettings || null;

    this.startedAtMs = null;
    this.endedAtMs = null;
    this.endedReason = null;
    this.errorMessage = null;
    this.byteLength = 0;

    this._chunks = [];
    this._cloneStream = null;
    this._cloneTrack = null;
    this._recorder = null;
    this._stopPromise = null;
    this._stopResolve = null;
    this._trackEndedHandler = null;
    this._stopped = false;
  }

  // Construct cloned track + MediaStream, instantiate MediaRecorder, and
  // start it. Throws synchronously on any failure so the caller can record
  // an error and move on (the error path is per-source, not per-recording).
  start(now = Date.now()) {
    const stream = this.source.stream;
    if (!stream || typeof stream.getVideoTracks !== "function") {
      throw new Error("source has no getVideoTracks()");
    }
    const tracks = stream.getVideoTracks() || [];
    const original = tracks[0];
    if (!original) throw new Error("source has no video track");

    if (typeof original.clone !== "function") {
      throw new Error("video track is not clonable");
    }
    const cloned = original.clone();
    this._cloneTrack = cloned;
    // Build a fresh MediaStream around the cloned track. We avoid touching
    // the original stream so the live preview / model-frame capture path is
    // unaffected.
    if (typeof MediaStream !== "undefined") {
      this._cloneStream = new MediaStream([cloned]);
    } else {
      // Test fallback: a trivial stand-in that exposes getVideoTracks.
      this._cloneStream = { getVideoTracks: () => [cloned], getTracks: () => [cloned] };
    }

    // Track-level "ended" — fired when the underlying device closes (e.g.
    // user resets the slot, OS revokes the camera, screen-share ends).
    this._trackEndedHandler = () => {
      this._finalizeOnTrackEnded();
    };
    if (typeof cloned.addEventListener === "function") {
      cloned.addEventListener("ended", this._trackEndedHandler, { once: true });
    } else {
      cloned.onended = this._trackEndedHandler;
    }

    let recorder;
    try {
      recorder = this.mimeType
        ? new this.MediaRecorderCtor(this._cloneStream, { mimeType: this.mimeType })
        : new this.MediaRecorderCtor(this._cloneStream);
    } catch (e) {
      // Fall back to default mime if a typed construction failed.
      recorder = new this.MediaRecorderCtor(this._cloneStream);
      this.mimeType = recorder.mimeType || "";
    }
    this._recorder = recorder;
    this.mimeType = recorder.mimeType || this.mimeType || "";

    const handleData = (ev) => {
      const blob = ev && ev.data;
      if (!blob || (typeof blob.size === "number" && blob.size === 0)) return;
      this._chunks.push(blob);
      this.byteLength += blob.size || 0;
    };
    const handleStop = () => {
      if (this._stopResolve) {
        this._stopResolve();
        this._stopResolve = null;
      }
    };
    const handleError = (ev) => {
      this.errorMessage = (ev && ev.error && ev.error.message) || "MediaRecorder error";
    };

    if (typeof recorder.addEventListener === "function") {
      recorder.addEventListener("dataavailable", handleData);
      recorder.addEventListener("stop", handleStop);
      recorder.addEventListener("error", handleError);
    } else {
      recorder.ondataavailable = handleData;
      recorder.onstop = handleStop;
      recorder.onerror = handleError;
    }

    recorder.start(TIMESLICE_MS);
    this.startedAtMs = now;
  }

  _finalizeOnTrackEnded() {
    if (this._stopped) return;
    if (!this.endedReason) this.endedReason = "track_ended";
    void this.stop("track_ended").catch(() => {});
  }

  async stop(reason = "stopped", now = Date.now()) {
    if (this._stopped) {
      // Idempotent: just await the existing stop promise if any.
      if (this._stopPromise) await this._stopPromise;
      return;
    }
    this._stopped = true;
    if (!this.endedReason) this.endedReason = reason;
    this.endedAtMs = now;

    const recorder = this._recorder;
    if (recorder && (recorder.state === "recording" || recorder.state === "paused")) {
      this._stopPromise = new Promise((resolve) => {
        this._stopResolve = resolve;
      });
      try {
        recorder.stop();
      } catch (e) {
        this.errorMessage = this.errorMessage || (e && e.message) || "stop failed";
        if (this._stopResolve) {
          this._stopResolve();
          this._stopResolve = null;
        }
      }
      // Defensive timeout: if onstop never fires, resolve after 5 s so we
      // don't deadlock the recording.
      const watchdog = setTimeout(() => {
        if (this._stopResolve) {
          this._stopResolve();
          this._stopResolve = null;
        }
      }, 5000);
      try {
        await this._stopPromise;
      } finally {
        clearTimeout(watchdog);
      }
    }

    // Release the clone independent of the original track.
    if (this._cloneTrack) {
      try {
        if (
          this._trackEndedHandler &&
          typeof this._cloneTrack.removeEventListener === "function"
        ) {
          this._cloneTrack.removeEventListener("ended", this._trackEndedHandler);
        }
        this._cloneTrack.stop();
      } catch (_) {
        /* already stopped */
      }
    }
  }

  // Combine chunks into a single Uint8Array. Resolves to a zero-length array
  // if no data was captured (e.g. recorder failed before first chunk).
  async finalizeBytes() {
    if (this._chunks.length === 0) return new Uint8Array(0);
    let blob;
    if (typeof Blob !== "undefined") {
      blob = new Blob(this._chunks, { type: this.mimeType || "application/octet-stream" });
    } else {
      // Test fallback path.
      const total = this._chunks.reduce(
        (acc, c) => acc + (c.size || c.byteLength || (c.length || 0)),
        0,
      );
      const out = new Uint8Array(total);
      let offset = 0;
      for (const c of this._chunks) {
        let part;
        if (typeof c.arrayBuffer === "function") part = await c.arrayBuffer();
        else part = c;
        const bytes =
          part instanceof Uint8Array ? part : new Uint8Array(part);
        out.set(bytes, offset);
        offset += bytes.byteLength;
      }
      this._chunks.length = 0;
      this.byteLength = out.byteLength;
      return out;
    }
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    this._chunks.length = 0;
    this.byteLength = bytes.byteLength;
    return bytes;
  }

  filename(extension = "webm") {
    const safeLabel = safeFileName(`${this.kind}-${this.label}`, this.kind || "source");
    return `media/source-${this.index}-${safeLabel}.${extension}`;
  }

  meta() {
    return {
      id: this.id,
      index: this.index,
      kind: this.kind,
      label: this.label,
      is_main: this.isMain,
      filename: this.filename(),
      mime_type: this.mimeType || null,
      approx_byte_length: this.byteLength,
      started_at_ms: this.startedAtMs,
      stopped_at_ms: this.endedAtMs,
      approx_duration_ms:
        this.startedAtMs && this.endedAtMs
          ? Math.max(0, this.endedAtMs - this.startedAtMs)
          : null,
      ended_reason: this.endedReason || "stopped",
      intrinsic_width: this.intrinsicWidth,
      intrinsic_height: this.intrinsicHeight,
      track_settings: this.trackSettings,
      error: this.errorMessage,
    };
  }
}

// ---- main controller -------------------------------------------------------

const RECORDER_VERSION = "1.0.0";
const SCHEMA = "vta-recorder/1";
const TEXT_README = `\
This archive was created entirely in your browser by the local recorder.

Important:
  - The recorder did NOT upload this archive or its contents to any server.
  - The live workflow may still send media to the backend during the
    session; the recorder does NOT capture or upload that path. It captures
    only the local browser-side video sources you saw in the UI.

Files:
  - manifest.json              recording metadata (sources, durations, settings)
  - media/                     one .webm per active video source, captured separately
  - debug/debug-log.ndjson     per-event debug log (newline-delimited JSON)
  - debug/debug-log.json       same data as a JSON array (for tools that prefer it)
  - README.txt                 this file
`;

function defaultStatusMessage(state, ctx) {
  switch (state) {
    case "off":
      return ctx.lastDownloadedFilename
        ? `Saved ${ctx.lastDownloadedFilename}`
        : "Off";
    case "workflow-armed":
      return "Armed; will start with next workflow";
    case "workflow-recording":
    case "free-recording":
      return ctx.sourceCount > 0
        ? `Recording ${ctx.sourceCount} source${ctx.sourceCount === 1 ? "" : "s"}`
        : "Recording debug log only; no active video sources";
    case "finalizing":
      return "Finalizing ZIP";
    case "error":
      return ctx.errorMessage ? `Error: ${ctx.errorMessage}` : "Error";
    default:
      return state;
  }
}

async function defaultTriggerDownload(bytes, fileName) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("download requires a browser environment");
  }
  const blob = new Blob([bytes], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => {
      try {
        window.URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    }, 60_000);
  }
}

export class RecorderController {
  constructor({
    runtimeConfig = null,
    onStatusChange = null,
    // Test hooks production code passes none.
    _zipWriterFactory = null,
    _triggerDownload = null,
    _addDebugListener = null,
    _now = null,
    _MediaRecorderCtor = null,
  } = {}) {
    this.runtimeConfig = runtimeConfig;
    this.onStatusChange = typeof onStatusChange === "function" ? onStatusChange : null;
    this._zipWriterFactory = _zipWriterFactory || (() => new ZipRecordingWriter());
    this._triggerDownload = _triggerDownload || defaultTriggerDownload;
    this._addDebugListener = _addDebugListener || addDebugEventListener;
    this._now = typeof _now === "function" ? _now : () => Date.now();
    this._MediaRecorderCtor =
      _MediaRecorderCtor ||
      (typeof MediaRecorder !== "undefined" ? MediaRecorder : null);

    this.state = "off";
    this.mode = null;
    this.workflowId = null;
    this.workflowTitle = null;
    this.errorMessage = null;
    this.lastDownloadedFilename = null;

    this._sourceRecorders = [];
    this._knownSourceIds = new Set();
    this._capturedDebugEvents = [];
    this._debugUnsubscribe = null;
    this._startedAtMs = null;
    this._stopReason = null;
    this._sessionStartedNotice = null;
  }

  // ---- public API ----------------------------------------------------------

  isWorkflowArmed() {
    return this.state === "workflow-armed";
  }
  isRecording() {
    return this.state === "workflow-recording" || this.state === "free-recording";
  }

  armWorkflowCapture({ workflowId = null, workflowTitle = null } = {}) {
    if (this.isRecording()) {
      throw new Error("cannot arm while a recording is in progress");
    }
    if (this.state !== "off" && this.state !== "workflow-armed") {
      throw new Error(`cannot arm from state ${this.state}`);
    }
    this.workflowId = workflowId || null;
    this.workflowTitle = workflowTitle || null;
    this.errorMessage = null;
    this._setState("workflow-armed");
    logDebug("recorder", "armed", { workflow_id: this.workflowId });
  }

  disarmWorkflowCapture() {
    if (this.state !== "workflow-armed") return;
    this.workflowId = null;
    this.workflowTitle = null;
    this._setState("off");
    logDebug("recorder", "disarmed");
  }

  async startWorkflowCapture({ liveSession, videoSources, workflowState } = {}) {
    if (this.state !== "workflow-armed") {
      // Idempotent: callers (LiveSession) may invoke this on every workflow
      // start; if not armed, do nothing.
      return false;
    }
    if (workflowState && workflowState.workflow_id) {
      this.workflowId = workflowState.workflow_id || this.workflowId;
      this.workflowTitle = workflowState.title || this.workflowTitle;
    }
    return await this._beginRecording({
      mode: "workflow",
      newState: "workflow-recording",
      videoSources,
      liveSession,
    });
  }

  async stopWorkflowCapture(reason = "workflow_stopped") {
    if (this.state !== "workflow-recording") return false;
    return await this._endRecording(reason);
  }

  async startFreeCapture({ liveSession = null, videoSources } = {}) {
    if (this.state !== "off") {
      // Defer to caller: free capture is exclusive.
      throw new Error(`cannot start free capture from state ${this.state}`);
    }
    return await this._beginRecording({
      mode: "free",
      newState: "free-recording",
      videoSources,
      liveSession,
    });
  }

  async stopFreeCapture(reason = "free_capture_stopped") {
    if (this.state !== "free-recording") return false;
    return await this._endRecording(reason);
  }

  syncSources(videoSources) {
    if (!this.isRecording()) return;
    if (!videoSources || typeof videoSources.getRecordableSources !== "function") return;

    const next = videoSources.getRecordableSources();
    let added = 0;
    const mimeType = this._chosenMimeType;
    for (const source of next) {
      if (this._knownSourceIds.has(source.id)) continue;
      try {
        const sr = new SourceRecorder({
          source,
          mimeType,
          MediaRecorderCtor: this._MediaRecorderCtor,
        });
        sr.start(this._now());
        this._sourceRecorders.push(sr);
        this._knownSourceIds.add(source.id);
        added += 1;
        logDebug("recorder", "source_attached_mid_recording", {
          source_id: source.id,
          source_index: source.index,
        });
      } catch (e) {
        logDebug(
          "recorder",
          "source_attach_failed",
          { source_id: source.id, message: e?.message || String(e) },
          "warn",
        );
      }
    }
    if (added > 0) this._notify();
  }

  statusPayload() {
    const ctx = {
      sourceCount: this._sourceRecorders.length,
      errorMessage: this.errorMessage,
      lastDownloadedFilename: this.lastDownloadedFilename,
    };
    return {
      state: this.state,
      mode: this.mode,
      workflow_id: this.workflowId,
      workflow_title: this.workflowTitle,
      source_count: ctx.sourceCount,
      message: defaultStatusMessage(this.state, ctx),
      error_message: this.errorMessage,
      last_downloaded_filename: this.lastDownloadedFilename,
    };
  }

  dispose() {
    // Best-effort teardown. Does NOT trigger a download.
    if (this._debugUnsubscribe) {
      try { this._debugUnsubscribe(); } catch (_) { /* ignore */ }
      this._debugUnsubscribe = null;
    }
    for (const sr of this._sourceRecorders) {
      void sr.stop("disposed").catch(() => {});
    }
    this._sourceRecorders = [];
    this._knownSourceIds.clear();
    this._capturedDebugEvents.length = 0;
    this.state = "off";
    this.mode = null;
  }

  // ---- internal ------------------------------------------------------------

  _setState(next) {
    if (this.state === next) return;
    this.state = next;
    this._notify();
  }

  _notify() {
    if (this.onStatusChange) {
      try {
        this.onStatusChange(this.statusPayload());
      } catch (_) {
        /* listener errors are isolated */
      }
    }
  }

  async _beginRecording({ mode, newState, videoSources, liveSession }) {
    if (this._MediaRecorderCtor == null) {
      this.errorMessage = "MediaRecorder is not available in this browser.";
      this._setState("error");
      logDebug("recorder", "media_recorder_unavailable", {}, "error");
      return false;
    }

    this.mode = mode;
    this.errorMessage = null;
    this.lastDownloadedFilename = null;
    this._capturedDebugEvents = [];
    this._sourceRecorders = [];
    this._knownSourceIds.clear();
    this._stopReason = null;
    this._startedAtMs = this._now();
    this._chosenMimeType = chooseMediaRecorderMimeType(this._MediaRecorderCtor);

    // Subscribe to the global debug stream first so we capture our own
    // `recorder_started` entry.
    try {
      this._debugUnsubscribe = this._addDebugListener((ev) =>
        this._onDebugEvent(ev),
      );
    } catch (e) {
      this.errorMessage = `failed to attach debug listener: ${e?.message || e}`;
      this._setState("error");
      return false;
    }

    logDebug("recorder", "recorder_started", {
      mode,
      workflow_id: this.workflowId,
      mime_type: this._chosenMimeType,
    });

    const sources =
      videoSources && typeof videoSources.getRecordableSources === "function"
        ? videoSources.getRecordableSources()
        : [];

    for (const source of sources) {
      try {
        const sr = new SourceRecorder({
          source,
          mimeType: this._chosenMimeType,
          MediaRecorderCtor: this._MediaRecorderCtor,
        });
        sr.start(this._startedAtMs);
        this._sourceRecorders.push(sr);
        this._knownSourceIds.add(source.id);
      } catch (e) {
        logDebug(
          "recorder",
          "source_start_failed",
          {
            source_id: source.id,
            source_index: source.index,
            message: e?.message || String(e),
          },
          "warn",
        );
      }
    }

    // Whether or not any video source attached, we still record the debug
    // log; that's the documented "Recording debug log only" mode.
    this._setState(newState);
    return true;
  }

  _onDebugEvent(event) {
    // Including the recorder's own debug events in the captured log is
    // useful for diagnosing a failed recording, and the events are
    // local-only. We bound the buffer to keep memory sane on long recordings.
    const cap = 100000;
    if (this._capturedDebugEvents.length >= cap) {
      this._capturedDebugEvents.shift();
    }
    this._capturedDebugEvents.push(event);
  }

  async _endRecording(reason) {
    this._stopReason = reason || "stopped";
    this._setState("finalizing");
    logDebug("recorder", "recorder_stopping", { reason: this._stopReason });

    // Detach the listener BEFORE we keep emitting "stopping" events that
    // would inflate the captured log indefinitely.
    if (this._debugUnsubscribe) {
      try { this._debugUnsubscribe(); } catch (_) { /* ignore */ }
      this._debugUnsubscribe = null;
    }

    const stoppedAtMs = this._now();

    // Stop all source recorders. Errors per-source are captured in the
    // SourceRecorder's `errorMessage` and do not abort finalization.
    await Promise.all(
      this._sourceRecorders.map((sr) =>
        sr.stop(this._stopReason, stoppedAtMs).catch((e) => {
          sr.errorMessage = sr.errorMessage || e?.message || String(e);
        }),
      ),
    );

    let archiveBytes = null;
    let filename = null;
    try {
      const writer = this._zipWriterFactory();
      // Add per-source media first.
      const sourceMetas = [];
      for (const sr of this._sourceRecorders) {
        try {
          const bytes = await sr.finalizeBytes();
          if (bytes && bytes.byteLength > 0) {
            writer.add(sr.filename(), bytes, { level: 0 });
          }
        } catch (e) {
          sr.errorMessage = sr.errorMessage || e?.message || String(e);
        }
        sourceMetas.push(sr.meta());
      }

      const debugBytes = this._encodeDebugLogNdjson(stoppedAtMs);
      writer.add("debug/debug-log.ndjson", debugBytes);
      const debugJsonBytes = this._encodeDebugLogJson(stoppedAtMs);
      writer.add("debug/debug-log.json", debugJsonBytes);

      const manifest = this._buildManifest({ sourceMetas, stoppedAtMs });
      writer.add(
        "manifest.json",
        new TextEncoder().encode(JSON.stringify(manifest, null, 2)),
      );
      writer.add("README.txt", new TextEncoder().encode(TEXT_README));

      archiveBytes = await writer.build();
      filename = this._defaultFilename(stoppedAtMs);
      await this._triggerDownload(archiveBytes, filename);
      this.lastDownloadedFilename = filename;
      logDebug("recorder", "recorder_saved", {
        filename,
        byte_length: archiveBytes.byteLength,
        source_count: this._sourceRecorders.length,
      });
    } catch (e) {
      this.errorMessage = e?.message || String(e);
      logDebug(
        "recorder",
        "recorder_finalize_failed",
        { message: this.errorMessage },
        "error",
      );
      this._setState("error");
      return false;
    } finally {
      this._sourceRecorders = [];
      this._knownSourceIds.clear();
      this._capturedDebugEvents.length = 0;
    }

    this.mode = null;
    this.workflowId = null;
    this.workflowTitle = null;
    this._setState("off");
    return true;
  }

  _buildManifest({ sourceMetas, stoppedAtMs }) {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const platform = typeof navigator !== "undefined" ? navigator.platform || "" : "";
    return {
      schema: SCHEMA,
      recorder_version: RECORDER_VERSION,
      mode: this.mode,
      stop_reason: this._stopReason || null,
      started_at_iso: nowIso(this._startedAtMs),
      started_at_ms: this._startedAtMs,
      stopped_at_iso: nowIso(stoppedAtMs),
      stopped_at_ms: stoppedAtMs,
      duration_ms: Math.max(0, (stoppedAtMs || 0) - (this._startedAtMs || 0)),
      workflow:
        this.mode === "workflow"
          ? { id: this.workflowId || null, title: this.workflowTitle || null }
          : null,
      sources: sourceMetas,
      debug_log: "debug/debug-log.ndjson",
      user_agent: ua,
      platform,
      tz_offset_minutes: -new Date().getTimezoneOffset(),
      // We deliberately do NOT include cookies, session ids, auth codes,
      // PINs, or any user identity beyond what is in the user_agent string.
    };
  }

  _encodeDebugLogNdjson(stoppedAtMs) {
    const header = {
      type: "vta_recorder_debug_log",
      version: RECORDER_VERSION,
      mode: this.mode,
      started_at_ms: this._startedAtMs,
      stopped_at_ms: stoppedAtMs,
    };
    const lines = [JSON.stringify(header)];
    for (const ev of this._capturedDebugEvents) {
      lines.push(JSON.stringify(ev));
    }
    return new TextEncoder().encode(`${lines.join("\n")}\n`);
  }

  _encodeDebugLogJson(stoppedAtMs) {
    const payload = {
      schema: "vta_recorder_debug_log",
      version: RECORDER_VERSION,
      mode: this.mode,
      started_at_ms: this._startedAtMs,
      stopped_at_ms: stoppedAtMs,
      events: this._capturedDebugEvents,
    };
    return new TextEncoder().encode(JSON.stringify(payload, null, 2));
  }

  _defaultFilename(stoppedAtMs) {
    const stamp = nowIso(stoppedAtMs).replace(/[:.]/g, "-");
    const tag = this.mode === "workflow"
      ? `workflow-${safeFileName(this.workflowId || "unknown", "workflow")}`
      : "free-capture";
    return `vta-recording-${tag}-${stamp}.zip`;
  }
}

// Internal helpers exposed for tests only.
export const _internals = {
  SourceRecorder,
  defaultTriggerDownload,
  TIMESLICE_MS,
};
